import React, { useEffect, useRef, useState, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { useCoinData } from '../../lib/useCoinData';
import { logger } from '../../lib/logger';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import LeverageWizard from './LeverageWizard';

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(v) ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: d }));
const fmtPrice = (v) => {
    if (!v && v !== 0) return '—';
    if (v >= 1000) return fmt(v, 2);
    if (v >= 1) return fmt(v, 4);
    return Number(v).toLocaleString('en-US', { maximumSignificantDigits: 4, useGrouping: false });
};

const RiskBadge = ({ level }) => {
    const map = {
        low: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
        medium: 'border-amber-400/30 bg-amber-400/10 text-amber-300',
        high: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
    };
    const labels = { low: 'Düşük', medium: 'Orta', high: 'Yüksek' };
    return (
        <span className={`inline-flex items-center border rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${map[level] || ''}`}>
            {labels[level] || level}
        </span>
    );
};

const Row = ({ label, value, valueClass = 'text-slate-100' }) => (
    <div className="flex items-center justify-between py-1.5 text-xs">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">{label}</span>
        <span className={`font-mono tabular-nums ${valueClass}`}>{value}</span>
    </div>
);

export default function PositionAnalyzer({ symbol, listVerdict }) {
    const [market, setMarket] = useState('spot');
    const [side, setSide] = useState('buy');
    const [entry, setEntry] = useState('');
    const [investmentUsd, setInvestmentUsd] = useState('');  // Kullanıcı USD giriyor
    const [leverage, setLeverage] = useState(10);
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [positions, setPositions] = useState([]);
    const [saving, setSaving] = useState(false);
    const { data: coinData } = useCoinData(symbol, 'short', 15000);
    const initFor = useRef(null);

    // Coin miktarı (boyut) USD ile entry'den otomatik hesaplanır
    const entryNum = parseFloat(entry) || 0;
    const usdNum = parseFloat(investmentUsd) || 0;
    // Spot'ta yatırım = USD; Futures'ta marjin = USD, nominal = USD × kaldıraç
    const notionalUsd = market === 'futures' ? usdNum * (parseFloat(leverage) || 1) : usdNum;
    const coinQty = entryNum > 0 ? notionalUsd / entryNum : 0;

    const fetchPositions = useCallback(async () => {
        try {
            const { data } = await apiClient.get('/positions');
            setPositions(data.positions || []);
        } catch (err) {
            logger.warn('positions fetch failed', err);
        }
    }, []);

    useEffect(() => {
        fetchPositions();
    }, [fetchPositions]);

    useEffect(() => {
        if (market === 'spot') setSide('buy');
        else if (side === 'buy') setSide('long');
    }, [market, side]);

    // Auto-fetch live coin data when symbol changes; auto-fill entry with current price.
    // Resets entry/leverage to NEW coin's values on every symbol change so the form
    // never carries stale numbers from a previously-selected coin.
    // New coin selected → old analysis and prices are invalid immediately
    useEffect(() => {
        setAnalysis(null);
        setEntry('');
        initFor.current = null;
    }, [symbol]);

    // Fill the form once per coin with that coin's fresh price / side / leverage
    useEffect(() => {
        if (!coinData || coinData.symbol !== symbol || initFor.current === symbol) return;
        initFor.current = symbol;
        if (coinData.price) setEntry(String(coinData.price));
        const lv = coinData.verdict?.levels;
        const dir = coinData.verdict?.direction;
        if (lv && dir === 'long') {
            setSide(market === 'futures' ? 'long' : 'buy');
        } else if (lv && dir === 'short') {
            setSide((s) => (market === 'futures' ? 'short' : s));
        }
        if (lv?.leverage) setLeverage(parseInt(String(lv.leverage), 10) || 1);
    }, [coinData, symbol]);  // eslint-disable-line

    // Tek karar: işlem planı verdict.levels'tan gelir (AÇ / KÜÇÜK AÇ / EMİR KOY); BEKLE / KOŞMUŞ'ta plan yok.
    const v = listVerdict || coinData?.verdict;
    const pullback = v?.action === 'KOVALAMA';
    const plan = v?.levels
        ? { side: v.direction === 'short' ? 'SHORT' : 'LONG', entry: v.levels.entry, tp1: v.levels.tp, sl: v.levels.stop,
            levLabel: v.levels.leverage, levValue: parseInt(String(v.levels.leverage || '1'), 10) || 1, label: `${v.emoji} ${v.action_tr}` }
        : null;

    const applyRecommendation = () => {
        if (!plan) {
            toast.error(`Karar ${v?.action_tr || 'BEKLE'} — aktif işlem planı yok`);
            return;
        }
        if (plan.side === 'SHORT' && market !== 'futures') setMarket('futures');
        setEntry(String(plan.entry ?? coinData.price ?? ''));
        setLeverage(plan.levValue);
        setSide(plan.side === 'SHORT' ? 'short' : market === 'futures' ? 'long' : 'buy');
        toast.success(`${plan.label} planı uygulandı: ${plan.levLabel || ''}`);
    };

    const analyze = async () => {
        if (!symbol || !entry || !investmentUsd) {
            toast.error('Sembol seç, giriş fiyatı ve yatırım miktarını gir');
            return;
        }
        if (coinQty <= 0) {
            toast.error('Yatırım miktarı geçersiz');
            return;
        }
        setLoading(true);
        try {
            const { data } = await apiClient.post('/positions/analyze', {
                symbol,
                market,
                side,
                entry_price: parseFloat(entry),
                size: coinQty,  // backend coin miktarı bekliyor
                leverage: market === 'futures' ? parseFloat(leverage) : 1,
                margin: market === 'futures' ? usdNum : undefined,  // Futures'ta yatırılan USD = margin
            });
            setAnalysis(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Analiz başarısız');
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (!analysis) {
            toast.error('Önce analiz et');
            return;
        }
        setSaving(true);
        try {
            await apiClient.post('/positions', {
                symbol,
                market,
                side,
                entry_price: parseFloat(entry),
                size: coinQty,
                leverage: market === 'futures' ? parseFloat(leverage) : 1,
                margin: market === 'futures' ? usdNum : undefined,
            });
            toast.success('Pozisyon kaydedildi');
            fetchPositions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kayıt başarısız');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await apiClient.delete(`/positions/${id}`);
            fetchPositions();
        } catch (err) {
            logger.warn('position delete failed', err);
        }
    };

    return (
        <div className="border border-slate-700 bg-slate-800 rounded-md overflow-hidden h-full flex flex-col" data-testid="position-analyzer-card">
            <div className="px-5 py-4 border-b border-slate-700">
                <h3 className="text-sm font-semibold tracking-tight text-slate-100">Pozisyon Analizi</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                    {symbol ? `${symbol.replace('USDT', '')}/USDT için manuel hesaplama` : 'Önce bir coin seç'}
                </p>
            </div>

            {coinData && (
                <div className="px-5 py-3 border-b border-slate-700 bg-slate-700/30 space-y-2" data-testid="position-recommendation-card">
                    <div className="flex items-baseline justify-between gap-2">
                        <div className="flex items-baseline gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-slate-400">Mevcut Fiyat</span>
                            <span data-testid="position-current-price" className="font-mono text-sm text-slate-100">
                                ${fmtPrice(coinData.price)}
                            </span>
                        </div>
                        <span className={`text-[11px] font-mono ${coinData.change_pct_24h >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {coinData.change_pct_24h >= 0 ? '+' : ''}{Number(coinData.change_pct_24h).toFixed(2)}%
                        </span>
                    </div>

                    {plan && (
                        <>
                            <div className="text-[9px] uppercase tracking-wider text-slate-400">
                                {pullback ? 'Geri çekilme limit planı — kovalama değil, sadece fiyat bu seviyeye sakin gelirse' : 'Karar planı — Adaylar listesi ve coin detayıyla aynı'}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
                                <div className="min-w-0">
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Giriş</div>
                                    <div className="text-slate-100 break-all">${fmtPrice(plan.entry)}</div>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Hedef (TP)</div>
                                    <div className="text-emerald-300 break-all">${fmtPrice(plan.tp1)}</div>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Stop</div>
                                    <div className="text-amber-300 break-all">${fmtPrice(plan.sl)}</div>
                                </div>
                            </div>
                            <div className="flex items-center justify-between text-[11px] gap-2">
                                <span data-testid="position-plan-label" className={`font-mono font-semibold ${plan.side === 'LONG' ? 'text-emerald-300' : 'text-rose-300'}`}>
                                    {plan.label} · {plan.side} · {plan.levLabel}
                                </span>
                                <button
                                    type="button"
                                    onClick={applyRecommendation}
                                    data-testid="apply-recommendation-btn"
                                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-600 hover:border-slate-400 hover:bg-slate-700 text-slate-200"
                                >
                                    {pullback ? 'Geri Çekilme Planını Uygula' : 'Planı Uygula'}
                                </button>
                            </div>
                            {coinData.recommendation?.leverage?.warning && (
                                <div className="text-[10px] text-amber-400/90 leading-snug">
                                    ⚠ {coinData.recommendation.leverage.warning}
                                </div>
                            )}
                        </>
                    )}
                    {!plan && (
                        <div className="text-[11px] text-slate-400" data-testid="position-plan-label">
                            Karar: <b className="text-slate-200">{v?.emoji} {v?.action_tr || 'BEKLE'}</b> — aktif işlem planı yok. Aşağıdaki hesaplama manueldir.
                        </div>
                    )}
                </div>
            )}

            <div className="p-5 space-y-4">
                {/* 🪄 KALDIRAÇ SİHİRBAZI - Risk-based position sizing */}
                <LeverageWizard
                    coinData={coinData}
                    onApply={({ entry: e, investment, leverage: lev, side: s }) => {
                        setEntry(String(e));
                        setInvestmentUsd(String(investment));
                        setLeverage(Number(lev));
                        if (s) setSide(s);
                        setMarket('futures');
                        toast.success('Sihirbaz sonucu forma uygulandı');
                    }}
                />

                <Tabs value={market} onValueChange={setMarket}>
                    <TabsList className="bg-transparent border-b border-slate-700 w-full justify-start rounded-none h-9 p-0" data-testid="position-market-tabs">
                        <TabsTrigger value="spot" data-testid="tab-spot" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-100 data-[state=active]:text-slate-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none text-xs uppercase tracking-wider">
                            Spot
                        </TabsTrigger>
                        <TabsTrigger value="futures" data-testid="tab-futures" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-100 data-[state=active]:text-slate-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none text-xs uppercase tracking-wider">
                            Futures
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="spot" className="mt-4 space-y-3" />
                    <TabsContent value="futures" className="mt-4 space-y-3" />
                </Tabs>

                {market === 'futures' && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSide('long')}
                            data-testid="side-long"
                            className={`flex-1 h-8 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                                side === 'long' ? 'bg-emerald-400/10 border-emerald-400/40 text-emerald-300' : 'border-slate-700 text-slate-300 hover:bg-slate-700'
                            }`}
                        >
                            Long
                        </button>
                        <button
                            onClick={() => setSide('short')}
                            data-testid="side-short"
                            className={`flex-1 h-8 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                                side === 'short' ? 'bg-rose-500/10 border-rose-500/40 text-rose-300' : 'border-slate-700 text-slate-300 hover:bg-slate-700'
                            }`}
                        >
                            Short
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 block">Giriş Fiyatı ($)</Label>
                        <Input value={entry} onChange={(e) => setEntry(e.target.value)} type="number" step="any" placeholder="0.00" data-testid="input-entry" className="bg-slate-900 border-slate-700 h-9 rounded-sm font-mono" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                            {market === 'futures' ? 'Marjin ($USD)' : 'Yatırım ($USD)'}
                        </Label>
                        <Input
                            value={investmentUsd}
                            onChange={(e) => setInvestmentUsd(e.target.value)}
                            type="number"
                            step="any"
                            placeholder="100"
                            data-testid="input-investment-usd"
                            className="bg-slate-900 border-slate-700 h-9 rounded-sm font-mono"
                        />
                    </div>
                    {market === 'futures' && (
                        <div className="col-span-2">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 block">Kaldıraç</Label>
                            <Input value={leverage} onChange={(e) => setLeverage(e.target.value)} type="number" data-testid="input-leverage" className="bg-slate-900 border-slate-700 h-9 rounded-sm font-mono" />
                        </div>
                    )}
                </div>

                {coinQty > 0 && (
                    <div className="text-[11px] text-slate-300 bg-slate-700/40 border border-slate-700/60 rounded-sm px-3 py-2" data-testid="coin-qty-helper">
                        ≈ <span className="font-mono text-slate-100">{fmt(coinQty, coinQty < 1 ? 6 : 4)}</span>{' '}
                        <span className="text-slate-400">{symbol?.replace('USDT', '')}</span>{' '}
                        {market === 'futures' && (
                            <span className="text-slate-400">(nominal değer ${fmt(notionalUsd, 2)})</span>
                        )}
                    </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                    <Button onClick={analyze} disabled={loading || !symbol} data-testid="analyze-button" className="flex-1 h-9 rounded-sm bg-emerald-500 text-white hover:bg-emerald-400 text-xs uppercase tracking-wider font-medium">
                        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Analiz Et'}
                    </Button>
                    <Button onClick={save} disabled={saving || !analysis} variant="outline" data-testid="save-position-button" className="h-9 rounded-sm border-slate-700 bg-transparent hover:bg-slate-700 text-xs uppercase tracking-wider">
                        <Plus className="h-3.5 w-3.5 mr-1" />Kaydet
                    </Button>
                </div>

                {analysis && (() => {
                    // TP1/TP2/SL fiyatlarına göre USD bazında potansiyel kâr/zararı hesapla
                    const rec = coinData?.recommendation;
                    const isLong = side === 'long' || side === 'buy';
                    const dirMul = isLong ? 1 : -1;
                    const calc = (target) => {
                        if (!target || !entryNum || coinQty <= 0) return null;
                        const pnl = (target - entryNum) * coinQty * dirMul;
                        const pnlPct = market === 'futures' && usdNum > 0
                            ? (pnl / usdNum) * 100
                            : entryNum > 0 ? ((target - entryNum) / entryNum) * 100 * dirMul : 0;
                        return { pnl, pnlPct };
                    };
                    const tp1 = rec?.tp1 ? calc(rec.tp1) : null;
                    const tp2 = rec?.tp2 ? calc(rec.tp2) : null;
                    const sl = rec?.stop_loss ? calc(rec.stop_loss) : null;
                    const sideLabel = isLong ? 'AL' : 'SAT';

                    return (
                        <div className="pt-2 border-t border-slate-700/50 space-y-3" data-testid="analysis-result">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">İşlem Planı</span>
                                <RiskBadge level={analysis.risk_level} />
                            </div>

                            <div className="bg-slate-700/50 border border-slate-700 rounded-sm p-3 space-y-2 text-xs leading-relaxed">
                                <div className="text-slate-200">
                                    <span className="text-slate-400">1.</span>{' '}
                                    <span className="font-mono text-slate-100">${fmt(usdNum, 2)}</span> ile{' '}
                                    <span className="font-mono text-slate-100">${fmtPrice(entryNum)}</span> seviyesinde{' '}
                                    <span className={`font-semibold ${isLong ? 'text-emerald-300' : 'text-rose-300'}`}>{sideLabel}</span> emri ver →{' '}
                                    <span className="font-mono text-slate-100">{fmt(coinQty, coinQty < 1 ? 6 : 4)} {symbol?.replace('USDT', '')}</span>{' '}
                                    {market === 'futures' && <span className="text-slate-400">({leverage}x kaldıraç)</span>}
                                </div>

                                {tp1 && (
                                    <div className="text-slate-200" data-testid="plan-tp1">
                                        <span className="text-slate-400">2.</span>{' '}
                                        <span className="font-mono text-emerald-300">${fmtPrice(rec.tp1)}</span>'a ulaşırsa →{' '}
                                        pozisyonun yarısını sat,{' '}
                                        <span className="font-mono font-semibold text-emerald-300">+${fmt(tp1.pnl / 2, 2)}</span>{' '}
                                        <span className="text-slate-400">(yarı sat)</span> kâr cebine
                                    </div>
                                )}

                                {tp2 && (
                                    <div className="text-slate-200" data-testid="plan-tp2">
                                        <span className="text-slate-400">3.</span>{' '}
                                        <span className="font-mono text-emerald-300">${fmtPrice(rec.tp2)}</span>'a ulaşırsa →{' '}
                                        kalan yarıyı da sat,{' '}
                                        <span className="font-mono font-semibold text-emerald-300">+${fmt(tp2.pnl / 2, 2)}</span>{' '}
                                        ekstra → <span className="text-slate-100">toplam{' '}
                                            <span className="font-mono font-semibold text-emerald-300">+${fmt((tp1?.pnl || 0) / 2 + tp2.pnl / 2, 2)}</span>{' '}
                                            kâr ({fmt((((tp1?.pnl || 0) / 2 + tp2.pnl / 2) / usdNum) * 100, 1)}%)
                                        </span>
                                    </div>
                                )}

                                {sl && (
                                    <div className="text-slate-200" data-testid="plan-sl">
                                        <span className="text-slate-400">⚠</span>{' '}
                                        Yanlış giderse (<span className="font-mono text-amber-300">${fmtPrice(rec.stop_loss)}</span>)
                                        → STOP-LOSS tetiklenir, kayıp:{' '}
                                        <span className="font-mono font-semibold text-rose-400">${fmt(Math.abs(sl.pnl), 2)}</span>{' '}
                                        ({fmt(Math.abs(sl.pnlPct), 1)}%)
                                    </div>
                                )}

                                {!rec || rec.action === 'WAIT' ? (
                                    <div className="text-amber-300/90 text-[11px]">
                                        Bu coin için aktif TP/SL önerisi yok. Yukarıdaki sayılar sadece pozisyon büyüklüğünüzü gösterir.
                                    </div>
                                ) : null}
                            </div>

                            <div className="space-y-1">
                                <Row label="Anlık PnL" value={`${analysis.pnl >= 0 ? '+' : ''}$${fmt(analysis.pnl, 2)} (${fmt(analysis.pnl_pct, 2)}%)`} valueClass={analysis.pnl >= 0 ? 'text-emerald-400' : 'text-rose-500'} />
                                {market === 'futures' && (
                                    <>
                                        <Row label="Likidasyon Fiyatı" value={`$${fmtPrice(analysis.liquidation_price)}`} valueClass="text-rose-400" />
                                        <Row label="Liq. Mesafesi" value={`${fmt(analysis.liquidation_distance_pct, 2)}%`} valueClass={analysis.liquidation_distance_pct > 15 ? 'text-emerald-400' : analysis.liquidation_distance_pct > 5 ? 'text-amber-400' : 'text-rose-400'} />
                                    </>
                                )}
                                <Row label="Risk Skoru" value={`${fmt(analysis.risk_score, 0)} / 100`} valueClass={analysis.risk_score > 60 ? 'text-rose-400' : analysis.risk_score > 30 ? 'text-amber-400' : 'text-emerald-400'} />
                            </div>
                        </div>
                    );
                })()}
            </div>

            {positions.length > 0 && (
                <div className="border-t border-slate-700 mt-auto" data-testid="saved-positions">
                    <div className="px-5 py-2 text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-700/50">
                        Kayıtlı pozisyonlar ({positions.length})
                    </div>
                    <div className="divide-y divide-slate-700/50 max-h-40 overflow-y-auto">
                        {positions.map((p) => (
                            <div key={p.id} className="px-5 py-2 flex items-center justify-between text-xs gap-2 hover:bg-slate-700/50" data-testid={`saved-position-${p.id}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-slate-100 font-medium truncate">{p.symbol}</span>
                                    <span className={`text-[10px] uppercase tracking-wider ${p.side === 'long' || p.side === 'buy' ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {p.market}·{p.side}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`font-mono ${p.analysis?.pnl >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                                        {p.analysis?.pnl >= 0 ? '+' : ''}{fmt(p.analysis?.pnl_pct, 2)}%
                                    </span>
                                    <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-rose-400" data-testid={`delete-position-${p.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
